import type { ReactNode } from 'react'

import { Box, Flex } from '@enechain/ecloud-designsystem'

/** DS `SideMenuLayoutContentLayout` 本文の外周 padding (spacing token 4 = 16px)。PageLayout では prop 化しない。 */
const CONTENT_INSET = 4

type Props = {
  /**
   * ページ最上部の通知 (Compact Alert / ワークフローの戻りバー)。コンテンツ枠の左・上・右の
   * 16px を打ち消して枠の端に寄せ、並べる間隔は 0 (UI 辞書 alert-placement.md §0
   * 「ページ最上部の外側の余白」)。通知自身の内側余白は変えない。
   * 何も描画しない通知 (戻りバーが null を返す等) だけの時は、通知の器を畳んで通知なしと同じ見た目にする。
   * undefined / null / false は「通知なし」。
   */
  notices?: ReactNode
  children: ReactNode
}

/**
 * `PageLayout` の本文を包む枠。
 *
 * 通知がある時: 通知の領域だけ左・上・右を -16px で枠の端まで広げ、本文側で 16px を戻す。下の 16px は
 * 枠の padding のまま残すので、高さは上へはみ出した分 (16px) だけ足す。本文の領域は
 * 通知の下の残り高さ (`flex: 1` + `minHeight: 0`) を取るので、`height: 100%` で伸びる本文
 * (PageBody 等) は通知が出ても枠の外へ溢れない。内容が高い本文は従来どおり外側の
 * スクロール領域でスクロールする (ここでは overflow を持たない)。React mock の `PageBody` の
 * `notices` と同じ計算。
 *
 * 通知が無い時: 同じ要素の木のまま `display: contents` で箱を消し、包まない時と同じ見た目にする。
 * 通知の有無で木を変えると、通知が出入りするたびに本文が作り直されて入力中の状態が消える
 * (スポットの画面の test で無限に作り直して heap が尽きた)。
 */
export const PageNoticeFrame = ({
  notices,
  children,
}: Props): React.ReactElement => {
  const hasNotices =
    notices !== undefined && notices !== null && notices !== false
  return (
    <Flex
      flexDirection="column"
      display={hasNotices ? 'flex' : 'contents'}
      width={hasNotices ? 'calc(100% + 32px)' : undefined}
      height={hasNotices ? 'calc(100% + 16px)' : undefined}
      marginTop={hasNotices ? -CONTENT_INSET : undefined}
      data-testid={hasNotices ? 'page-notice-frame' : undefined}
      marginX={hasNotices ? -CONTENT_INSET : undefined}
    >
      <Flex
        flexDirection="column"
        flexShrink={0}
        gap={0}
        display={hasNotices ? 'flex' : 'none'}
        css={{ '&:empty': { display: 'none' } }}
      >
        {hasNotices ? notices : null}
      </Flex>
      <Box
        flex={hasNotices ? '1' : undefined}
        display={hasNotices ? 'block' : 'contents'}
        minHeight={hasNotices ? '0' : undefined}
        paddingTop={hasNotices ? CONTENT_INSET : undefined}
        paddingX={hasNotices ? CONTENT_INSET : undefined}
      >
        {children}
      </Box>
    </Flex>
  )
}
